import React, { useState, useEffect, useRef, useCallback } from "react";
import { BringzzPageContainer, BringzzIcon, BringzzText, BringzzButton } from "@bringzz/components";
import { useNavigation } from '../../../context/NavigationContext';
import MapInitializer from '../../Map/components/MapInitializer';

const AddressLocationScreen = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { navigate } = useNavigation();
  const [selectedAddress, setSelectedAddress] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCoords, setSelectedCoords] = useState({
    latitude: null,
    longitude: null,
  });
  const [map, setMap] = useState(null);
  const [locationMarker, setLocationMarker] = useState(null);
  const [addressDetails, setAddressDetails] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const mapRef = useRef(null);

  const logMapEvent = useCallback((event, data) => {
  }, []);

  const { initializeMap } = MapInitializer({
    onMapCreated: useCallback((newMap) => {
      setMap(newMap);
      if (newMap) {
        newMap.addEventListener('single-tap', (event) => {
          const coordinate = event.pointOnPage;
          const mapCoordinate = newMap.convertPointOnPageToCoordinate(coordinate);
          
          setSelectedCoords({
            latitude: mapCoordinate.latitude,
            longitude: mapCoordinate.longitude
          });
          
          reverseGeocode(mapCoordinate.latitude, mapCoordinate.longitude);
        });
      }
    }, []),
    logMapEvent
  });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setSelectedCoords(coords);
          
          setTimeout(() => {
            initializeMap(mapRef, coords);
          }, 100);
        },
        (error) => {
          console.error("Error getting location", error);
          setTimeout(() => {
            initializeMap(mapRef);
          }, 100);
        }
      );
    } else {
      setTimeout(() => {
        initializeMap(mapRef);
      }, 100);
    }
  }, [initializeMap]);

  const addLocationMarker = useCallback((latitude, longitude) => {
    if (!map || !window.mapkit) return;

    if (locationMarker) {
      map.removeAnnotation(locationMarker);
    }

    const coordinate = new window.mapkit.Coordinate(latitude, longitude);
    const marker = new window.mapkit.MarkerAnnotation(coordinate, {
      title: "Selected Location",
      color: "#007AFF",
      glyphText: "📍"
    });

    map.addAnnotation(marker);
    setLocationMarker(marker);

    const region = new window.mapkit.CoordinateRegion(
      coordinate,
      new window.mapkit.CoordinateSpan(0.01, 0.01)
    );
    map.setRegionAnimated(region, true);
  }, [map, locationMarker]);

  const reverseGeocode = useCallback((latitude, longitude) => {
    if (!window.mapkit) return;

    setSelectedCoords({
      latitude: latitude,
      longitude: longitude
    });

    const geocoder = new window.mapkit.Geocoder({
      language: "en-US"
    });

    const coordinate = new window.mapkit.Coordinate(latitude, longitude);
    
    geocoder.reverseLookup(coordinate, (error, data) => {
      if (error) {
        console.error('Reverse geocoding error:', error);
        return;
      }

      if (data && data.results && data.results.length > 0) {
        const result = data.results[0];
        const fullAddress = result.formattedAddress;
        setSelectedAddress(fullAddress);
        setSearchQuery(fullAddress);
        parseAddressFromPlace(result);
        addLocationMarker(latitude, longitude);
      }
    });
  }, [addLocationMarker]);

  const parseAddressFromPlace = useCallback((place) => {
    const details = {
      streetName: place.thoroughfare || '',
      houseNumber: place.subThoroughfare || '',
      cityName: place.locality || place.administrativeArea || '',
      postalCode: place.postalCode || place.postCode || '',
      countryName: place.country || ''
    };
    setAddressDetails(details);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    if (!window.mapkit) {
      console.log('MapKit not available');
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    console.log('Searching for:', query);

    try {
      const searchOptions = {
        language: "en-US",
        includeAddresses: true,
        includePointsOfInterest: true
      };

      if (selectedCoords.latitude && selectedCoords.longitude) {
        searchOptions.region = new window.mapkit.CoordinateRegion(
          new window.mapkit.Coordinate(selectedCoords.latitude, selectedCoords.longitude),
          new window.mapkit.CoordinateSpan(0.5, 0.5)
        );
      }

      const search = new window.mapkit.Search(searchOptions);

      search.search(query, async (error, data) => {
        setIsSearching(false);
        
        if (error) {
          console.error('Apple Maps search error:', error);
          setSuggestions([]);
          return;
        }
        
        if (data && data.places && data.places.length > 0) {
          const mappedSuggestions = await Promise.all(
            data.places.slice(0, 8).map(async (place, index) => {
              let enhancedPlace = { ...place };
              const missingPostalCode = !place.postalCode && !place.postCode;
              const missingStreetName = !place.thoroughfare;
                            
              if (missingPostalCode || missingStreetName) {
                try {
                  const nominatimResponse = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${place.coordinate.latitude}&lon=${place.coordinate.longitude}&addressdetails=1`
                  );
                  const nominatimData = await nominatimResponse.json();
                  
                  if (nominatimData.address) {
                    console.log('Nominatim data for enhancement:', nominatimData.address);
                    if (missingPostalCode && nominatimData.address.postcode) {
                      enhancedPlace.postalCode = nominatimData.address.postcode;
                      console.log('Added postal code:', nominatimData.address.postcode);
                    }
                    if (missingStreetName && nominatimData.address.road) {
                      enhancedPlace.thoroughfare = nominatimData.address.road;
                      console.log('Added street name:', nominatimData.address.road);
                    }
                  }
                } catch (nominatimError) {
                  console.warn('Nominatim enhancement failed:', nominatimError);
                }
              }
              return {
                name: place.name || enhancedPlace.thoroughfare || place.formattedAddress?.split(',')[0] || `Location ${index + 1}`,
                formattedAddress: place.formattedAddress || place.name || 'No address available',
                coordinate: place.coordinate,
                place: enhancedPlace,
                source: 'apple'
              };
            })
          );
          
          setSuggestions(mappedSuggestions);
        } else {
          setSuggestions([]);
        }
      });
    } catch (error) {
      console.error('Apple Maps search error:', error);
      setSuggestions([]);
      setIsSearching(false);
    }
  }, [selectedCoords]);

  const [isAddressSelected, setIsAddressSelected] = useState(false);

  const handleSuggestionClick = useCallback(async (suggestion) => {
    const coords = {
      latitude: suggestion.coordinate.latitude,
      longitude: suggestion.coordinate.longitude,
    };
    setSelectedCoords(coords);
    
    const fullAddress = suggestion.formattedAddress || suggestion.name;
    setSelectedAddress(fullAddress);
    setSearchQuery(fullAddress);
    setIsAddressSelected(true);
    
    parseAddressFromPlace(suggestion.place);
    
    addLocationMarker(coords.latitude, coords.longitude);
    setSuggestions([]);
  }, [parseAddressFromPlace, addLocationMarker]);

  const handleSaveAddress = useCallback(() => {
    if (!selectedAddress || !selectedCoords.latitude || !selectedCoords.longitude) {
      alert("Please select a valid address.");
      return;
    }
      
    const searchParams = new URLSearchParams(window.location.search);
    const isEdit = searchParams.has('edit');
    const isNew = searchParams.get('new') === 'true';
    
    const params = new URLSearchParams({
      geoLatitude: selectedCoords.latitude,
      geoLongitude: selectedCoords.longitude,
      selectedAddress: selectedAddress
    });
    if (addressDetails) {
      if (addressDetails.streetName) params.append('streetName', addressDetails.streetName);
      if (addressDetails.cityName) params.append('cityName', addressDetails.cityName);
      if (addressDetails.postalCode) params.append('postalCode', addressDetails.postalCode);
      if (addressDetails.countryName) params.append('countryName', addressDetails.countryName);
    }
        
    let navigateUrl;
    if (isEdit) {
      const editIntegrationId = searchParams.get('edit');
      params.append('edit', editIntegrationId);
      navigateUrl = `/settings/account/address?${params.toString()}`;
    } else if (isNew) {
      params.append('new', 'true');
      navigateUrl = `/settings/account/address?${params.toString()}`;
    }
  
    navigate(navigateUrl);
  }, [selectedAddress, selectedCoords, addressDetails, navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSuggestions(searchQuery);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, fetchSuggestions]);

  return (
    <BringzzPageContainer
      header={{
        left: (
          <BringzzIcon
            size="22"
            icon="IconChevronLeft"
            folder="LineIcons"
            className="cursor-pointer"
            onClick={() => navigate(-1)}
          />
        ),
        title: <BringzzText tag="h2">Set Location</BringzzText>,
      }}
    >
      <div className="flex flex-col h-full">
          <div className="relative flex-1">
            <div 
              ref={mapRef}
              className="w-full h-full"
              style={{ minHeight: '400px' }}
            />
            
            <div className="absolute top-4 left-4 right-4 z-10">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for an address..."
                  className="w-full p-4 pr-12 bg-white rounded-lg shadow-lg border border-gray-200 text-sm"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsAddressSelected(false);
                  }}
                />
                <BringzzIcon
                  icon="IconSearch"
                  folder="LineIcons"
                  size="20"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
              </div>
              
              {(suggestions.length > 0 || isSearching) && searchQuery.length >= 2 && !isAddressSelected && (
                <div className="mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                  {isSearching ? (
                    <div className="p-4 text-center text-gray-500">
                      <BringzzText tag="p">Searching...</BringzzText>
                    </div>
                  ) : (
                    suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        <BringzzText tag="p" className="font-medium text-sm">
                          {suggestion.name}
                        </BringzzText>
                        <BringzzText tag="p" className="text-xs text-gray-500 mt-1">
                          {suggestion.formattedAddress}
                        </BringzzText>
                        {suggestion.place.postalCode && (
                          <BringzzText tag="p" className="text-xs text-purple-600 mt-1">
                          {suggestion.place.postalCode}
                          </BringzzText>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

          </div>
          {selectedAddress && (
            <div className="bg-white border-t border-gray-200 p-4">
              <BringzzText tag="h4" className="font-medium mb-2">
                Selected Location
              </BringzzText>
              <BringzzText tag="p" className="text-sm text-gray-600">
                {selectedAddress}
              </BringzzText>
            </div>
          )}
          <div className="bg-white border-t border-gray-200 p-4">
            <BringzzButton
              className="w-full bg-magic-lilac text-white"
              onClick={handleSaveAddress}
              disabled={!selectedAddress}
            >
              Confirm Location
            </BringzzButton>
          </div>
      </div>
    </BringzzPageContainer>
  );
};

export default AddressLocationScreen;